'use client';
import React, { useState } from 'react';
import { Bookmark, MessageCircle, Clock, Sparkles, Share2, Heart, Flag } from 'lucide-react';
import type { FeedPost } from './FeedList';
import CommentsSection from './CommentsSection';
import Link from 'next/link';
import ReportModal from '@/components/ReportModal';

interface Props {
  post: FeedPost;
  isLiked: boolean;
  isSaved: boolean;
  onLike: () => void;
  onSave: () => void;
  isLoggedIn: boolean;
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getInitial(name: string | null) {
  return name ? name.charAt(0).toUpperCase() : '?';
}

export default function EditorialCard({ post, isLiked, isSaved, onLike, onSave, isLoggedIn }: Props) {
  const [commentOpen, setCommentOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  return (
    <article className="bg-white rounded-2xl border border-cream-300 overflow-hidden hover:shadow-md hover:border-cream-400 transition-all duration-200 group">
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <Link href={`/profile/${post.author?.username || post.author?.id}`}>
              {post.author?.avatar_url ? (
                <img src={post.author.avatar_url} alt={post.author.display_name || ''} className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-300 to-gold-500 flex items-center justify-center text-sm font-bold text-white shrink-0">
                  {getInitial(post.author?.display_name)}
                </div>
              )}
            </Link>
            <div>
              <div className="flex items-center gap-1.5">
                <Link href={`/profile/${post.author?.username || post.author?.id}`} className="text-sm font-semibold text-charcoal-900 hover:text-gold-600 transition-colors">
                  {post.author?.display_name || post.author?.username || 'Unknown'}
                </Link>
                {post.author?.role === 'icon' && (
                  <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-gold-50 border border-gold-200 rounded-full">
                    <Sparkles size={9} className="text-gold-500" />
                    <span className="text-xs font-medium text-gold-600" style={{ fontSize: '10px' }}>Icon</span>
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-charcoal-400 mt-0.5">
                <span>@{post.author?.username}</span>
                <span>·</span>
                <span>{formatDate(post.created_at)}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button className="p-1.5 rounded-lg hover:bg-cream-200 text-charcoal-400">
              <Share2 size={15} />
            </button>
            {isLoggedIn && (
              <button onClick={() => setReportOpen(true)} className="p-1.5 rounded-lg hover:bg-red-50 text-charcoal-400 hover:text-red-500 transition-colors">
                <Flag size={15} />
              </button>
            )}
          </div>
        </div>

        {/* Tags */}
        {post.tags?.length > 0 && (
          <div className="flex gap-2 mb-3 flex-wrap">
            {post.tags.map((tag) => (
              <span key={tag} className="px-2.5 py-1 bg-cream-200 text-charcoal-600 rounded-full text-xs font-medium">{tag}</span>
            ))}
          </div>
        )}

        {/* Cover Image */}
        {post.cover_image_url && (
          <div className="mb-4 rounded-xl overflow-hidden aspect-video">
            <img src={post.cover_image_url} alt={post.title || 'Editorial cover'} className="w-full h-full object-cover" />
          </div>
        )}

        {/* Title */}
        <h2 className="font-editorial text-xl font-semibold text-charcoal-900 leading-snug mb-3 hover:text-gold-600 cursor-pointer transition-colors">
          {post.title}
        </h2>

        {/* Body excerpt */}
        {post.body && (
          <p className="text-sm text-charcoal-600 leading-relaxed line-clamp-3">{post.body}</p>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 border-t border-cream-200 bg-cream-50 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={onLike} disabled={!isLoggedIn}
            className={`flex items-center gap-1.5 text-sm font-medium transition-all duration-150 active:scale-95 ${isLiked ? 'text-red-500' : 'text-charcoal-400 hover:text-charcoal-700'} disabled:opacity-50`}>
            <Heart size={15} className={isLiked ? 'fill-red-500' : ''} />
            <span className="tabular-nums">{post.like_count}</span>
          </button>
          <button onClick={onSave} disabled={!isLoggedIn}
            className={`flex items-center gap-1.5 text-sm font-medium transition-all duration-150 active:scale-95 ${isSaved ? 'text-gold-500' : 'text-charcoal-400 hover:text-charcoal-700'} disabled:opacity-50`}>
            <Bookmark size={15} className={isSaved ? 'fill-gold-400' : ''} />
            <span className="tabular-nums">{post.save_count}</span>
          </button>
          <button onClick={() => setCommentOpen(!commentOpen)}
            className="flex items-center gap-1.5 text-sm font-medium text-charcoal-400 hover:text-charcoal-700 transition-colors">
            <MessageCircle size={15} />
            <span className="tabular-nums">{post.comment_count}</span>
          </button>
        </div>
        <div className="flex items-center gap-1 text-xs text-charcoal-400">
          <Clock size={12} />
          Editorial
        </div>
      </div>

      {commentOpen && (
        <div className="border-t border-cream-200">
          <CommentsSection postId={post.id} isLoggedIn={isLoggedIn} />
        </div>
      )}

      {reportOpen && (
        <ReportModal postId={post.id} onClose={() => setReportOpen(false)} />
      )}
    </article>
  );
}